import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { useLocation } from "wouter";

const waitingListSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  agencyName: z.string().min(1, "Agency name is required"),
  hostAgency: z.string().optional(),
});

type WaitingListForm = z.infer<typeof waitingListSchema>;

export default function WaitingList() {
  const [, setLocation] = useLocation();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<WaitingListForm>({
    resolver: zodResolver(waitingListSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      agencyName: "",
      hostAgency: "",
    },
  });

  const onSubmit = async (data: WaitingListForm) => {
    try {
      // TODO: Implement API call to save waiting list entry
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting waiting list form:", error);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <div className="flex justify-center mb-6">
                <div className="p-4 rounded-full bg-gradient-to-br from-orange-500/20 to-emerald-500/20">
                  <CheckCircle className="h-16 w-16 text-orange-400" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                You're on the list!
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                We'll notify you as soon as AIVIA is ready for travel professionals. 
                Get ready to transform your productivity.
              </p>
              <Button 
                onClick={() => setLocation("/")}
                className="bg-gradient-to-r from-orange-500 to-emerald-500 hover:from-orange-600 hover:to-emerald-600 text-white px-8 py-4 text-lg font-semibold rounded-2xl"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Button 
              variant="ghost" 
              onClick={() => setLocation("/")}
              className="mb-8 text-gray-400 hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Join the <span className="text-transparent bg-gradient-to-r from-orange-300 to-emerald-300 bg-clip-text">AIVIA</span> Waiting List
            </h1>
            
            <p className="text-xl text-gray-300 mb-8">
              Be among the first travel professionals to experience the future of productivity. 
              We'll notify you when AIVIA is ready to transform your workflow.
            </p>
          </div>

          {/* Form Card */}
          <Card className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-bold text-white">
                Reserve Your Spot
              </CardTitle>
            </CardHeader>
            <CardContent className="px-8 pb-8">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">First Name *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your first name"
                              {...field}
                              className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-orange-400 focus:ring-orange-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">Last Name *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your last name"
                              {...field}
                              className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-orange-400 focus:ring-orange-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">Email Address *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter your email address"
                            {...field}
                            className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="agencyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">Agency Name *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter your agency name"
                            {...field}
                            className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="hostAgency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">Host Agency</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter your host agency (optional)"
                            {...field}
                            className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-orange-400 focus:ring-orange-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-500 to-emerald-500 hover:from-orange-600 hover:to-emerald-600 text-white py-4 text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-orange-500/25 rounded-2xl"
                    disabled={form.formState.isSubmitting}
                  >
                    {form.formState.isSubmitting ? "Joining..." : "Join the Waiting List"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <div className="text-center mt-12">
            <p className="text-gray-400 text-sm">
              By joining our waiting list, you'll be the first to know when AIVIA launches. 
              We respect your privacy and won't share your information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}